import styles from './TimePicker.module.scss';

import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';

import { Calendar, Callout, DayOfWeek, DirectionalHint, Label, Link, Stack, TextField, VerticalDivider } from '@fluentui/react';
import { css } from '../../util/Css';
import { DayPickerStrings } from '../../util/String';

const DATE_FORMAT = 'MM/DD/YYYY';
const TIME_FORMAT = 'h:mm a';
const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;

const hourOptions = Array.from({ length: 12 }, (_, i) => ({ text: '' + (i + 1), key: '' + (i + 1) }));
const minuteOptions = Array.from({ length: 60 }, (_, i) => ({ text: '' + i, key: '' + i }));
const meridiemOptions = [
  { text: 'am', key: 'am' },
  { text: 'pm', key: 'pm' },
];

interface Props {
  value?: Moment;
  onChange?: (value: Moment) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export const DateTimePicker = ({ value, onChange, label, required, disabled }: Props) => {
  const [datetime, setDatetime] = useState(value ? moment(value) : moment());
  const [parts, setParts] = useState({ hour: datetime.format('h'), minute: datetime.format('m'), meridiem: datetime.format('a') });

  // display text for the input control
  const [text, setText] = useState(datetime.format(DATE_TIME_FORMAT));

  const [openCallout, setOpenCallout] = useState(false);

  const wrapperDiv = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const current = moment(text, DATE_TIME_FORMAT);
    if (current.isValid()) {
      if (!openCallout) {
        setParts({
          hour: current.format('h'),
          minute: current.format('m'),
          meridiem: current.format('a'),
        });
        setDatetime(current);
      }
      if (onChange) {
        onChange(current);
      }
    }
    // Intentional to not have onChange, openCallout in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  // update the text if the time parts or date has changed when the callout is open
  useEffect(() => {
    if (openCallout) {
      const updated = moment(`${datetime.format(moment.HTML5_FMT.DATE)}T${parts.hour}:${parts.minute} ${parts.meridiem}`, `${moment.HTML5_FMT.DATE}T${TIME_FORMAT}`);
      setText(updated.format(DATE_TIME_FORMAT));
    }
    // Intentional to not have openCallout in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, datetime]);

  const resetToNow = () => {
    const m = moment();
    setParts({
      hour: m.format('h'),
      minute: m.format('m'),
      meridiem: m.format('a'),
    });
    setDatetime(m);
    setText(m.format(DATE_TIME_FORMAT));
    setOpenCallout(false);
  };

  const onTextChange = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
    setOpenCallout(false);
    setText(value || '');
  };

  return (
    <>
      <div>
        {label && <Label required={required}>{label}</Label>}
        <div ref={wrapperDiv}>
          <TextField
            disabled={disabled}
            value={text}
            iconProps={{
              iconName: 'DateTime',
              style: { pointerEvents: 'auto', cursor: 'pointer' },
              onClick: () => {
                if (disabled) {
                  return;
                }
                setOpenCallout(true);
              },
            }}
            onChange={onTextChange}
          />
        </div>
      </div>

      {openCallout && (
        <Callout target={wrapperDiv.current} directionalHint={DirectionalHint.bottomLeftEdge} beakWidth={0} onDismiss={() => setOpenCallout(!openCallout)} setInitialFocus>
          <Stack horizontal>
            <Calendar
              onSelectDate={(d) => setDatetime(moment(d))}
              isMonthPickerVisible={false}
              showGoToToday={false}
              strings={DayPickerStrings}
              value={datetime.toDate()}
              firstDayOfWeek={DayOfWeek.Sunday}
            />
            <VerticalDivider styles={{ divider: { height: 200, marginTop: 8, marginBottom: 8 } }} />
            <Stack>
              <Stack horizontal>
                <TimePart options={hourOptions} onSelect={(k) => setParts({ ...parts, hour: k })} value={parts.hour} />
                <TimePart options={minuteOptions} onSelect={(k) => setParts({ ...parts, minute: k })} value={parts.minute} />
                <TimePart options={meridiemOptions} onSelect={(k) => setParts({ ...parts, meridiem: k })} value={parts.meridiem} />
              </Stack>
            </Stack>
          </Stack>
          <Link className={styles.nowLink} onClick={resetToNow}>
            Set date/time now
          </Link>
        </Callout>
      )}
    </>
  );
};

interface TimePartProps {
  options: { text: string; key: string }[];
  onSelect: (value: string) => void;
  value: string;
}

const TimePart = ({ options, onSelect, value }: TimePartProps) => {
  const refs: { [key: string]: any } = options.reduce((acc: { [key: string]: React.RefObject<unknown> }, value) => {
    acc[value.key] = React.createRef();
    return acc;
  }, {});

  useEffect(() => {
    refs[value].current.scrollIntoView();
    // Intentional to have empty dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.timePartContainer}>
      {options.map((o) => (
        <div ref={refs[o.key]} className={css(styles.item, { [styles.selected]: o.key === value })} key={o.key} onClick={() => onSelect(o.key)}>
          {o.key}
        </div>
      ))}
    </div>
  );
};
